import { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const TOAST = { position: "bottom-center", autoClose: 5000 };

const csrfToken = () => {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute("content") : null;
};

const jsonHeaders = () => ({
  "X-CSRF-Token": csrfToken(),
  Accept: "application/json",
  "Content-Type": "application/json",
});

// With responseType: "blob" an error body arrives as a Blob, so the JSON has to be read
// back out before anything can inspect error.response.data.error.
export const readBlobError = async (error) => {
  const data = error && error.response && error.response.data;
  if (typeof Blob === "undefined" || !(data instanceof Blob)) return error;

  try {
    error.response.data = JSON.parse(await data.text());
  } catch (e) {
    // Not JSON after all -- leave the Blob in place and let the caller fall through.
  }
  return error;
};

// Shared handling for the 402 every generation endpoint can return.
// Returns true when this was a billing block and the user has been told about it.
export const handleBillingError = (error) => {
  if (!error || !error.response || error.response.status !== 402) return false;

  const detail = (error.response.data && error.response.data.error) || {};
  const billingUrl = detail.billing_url || "/users/billing";

  toast.error(detail.message || "A subscription is required to generate certificates.", {
    ...TOAST,
    autoClose: 6000,
  });

  // Every block is fixable on the billing page now: subscribe/resume for the
  // subscription codes, upgrade or buy a credit pack for the limit codes.
  window.setTimeout(() => {
    window.location.href = billingUrl;
  }, 1800);

  return true;
};

export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const filenameFrom = (response, fallback) => {
  const disposition = response.headers && response.headers["content-disposition"];
  if (!disposition) return fallback;

  const match = /filename[^;=\n]*=(?:(\\?['"])(.*?)\1|([^;\n]*))/.exec(disposition);
  const name = match && (match[2] || match[3]);
  return name ? name.trim() : fallback;
};

const useBilling = (initial = null) => {
  const [billing, setBilling] = useState(initial);
  const [loading, setLoading] = useState(false);

  // Server-side redirect flow shared by subscribe / change plan / buy credits: we ask
  // Rails for a PayPal approval URL and send the browser there. No PayPal SDK, no
  // credentials in the page. Some plan changes complete without an approval step and
  // come back as a fresh billing summary instead.
  const startCheckout = (path, payload, { successMessage } = {}) => {
    setLoading(true);

    axios
      .post(path, payload, { headers: jsonHeaders(), withCredentials: true })
      .then((response) => {
        const data = response.data || {};

        if (data.approve_url) {
          window.location.href = data.approve_url;
          return; // navigating away: deliberately stay in the loading state
        }

        if (data.status) {
          // Completed server-side (an approval-free plan change): fresh summary.
          setBilling(data);
          if (successMessage) toast.success(successMessage, TOAST);
          setLoading(false);
          return;
        }

        toast.error("PayPal did not return a checkout link. Please try again.", TOAST);
        setLoading(false);
      })
      .catch((error) => {
        const detail = (error.response && error.response.data && error.response.data.error) || {};
        toast.error(detail.message || "Could not start checkout. Please try again.", TOAST);
        setLoading(false);
      });
  };

  const subscribe = (tier) =>
    startCheckout("/users/billing/subscribe", tier ? { tier } : {});

  const changePlan = (tier) =>
    startCheckout(
      "/users/billing/change_plan",
      { tier },
      { successMessage: "Your plan has been updated." }
    );

  const buyCredits = (packKey) =>
    startCheckout("/users/billing/credits/order", { pack: packKey });

  const cancel = () => {
    const confirmed = window.confirm(
      "Cancel your subscription? You keep access until the end of the period you have already paid for."
    );
    if (!confirmed) return;

    setLoading(true);

    axios
      .post("/users/billing/cancel", {}, { headers: jsonHeaders(), withCredentials: true })
      .then((response) => {
        setBilling(response.data);
        toast.success("Your subscription has been cancelled.", TOAST);
      })
      .catch((error) => {
        const detail = (error.response && error.response.data && error.response.data.error) || {};
        toast.error(detail.message || "Could not cancel the subscription. Please try again.", TOAST);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchStatus = () =>
    axios
      .get("/users/billing/status", { headers: { Accept: "application/json" }, withCredentials: true })
      .then((response) => {
        setBilling(response.data);
        return response.data;
      })
      .catch(() => null);

  return { billing, setBilling, loading, subscribe, changePlan, buyCredits, cancel, fetchStatus };
};

export default useBilling;
